/**
 * Shapes returned by the control plane.
 *
 * These mirror the Rust types in `crates/xinfer-api`. Nothing in either
 * language enforces that, so `crates/xinfer-api/tests/openapi.rs` asserts that
 * every field named here is actually produced by a handler — a renamed field
 * fails that test rather than becoming `undefined` in a chart.
 */

/** Never `secret_hash`: the value the gate authenticates with stays server-side. */
export interface ApiKey {
  key_id: string;
  account_id: string;
  /** Last four characters, so a human can tell two keys apart. */
  hint: string;
  revoked: boolean;
  created_at: string;
  last_used_at: string | null;
}

/** The plaintext, returned exactly once and never again. */
export interface MintedKey {
  key: string;
  key_id: string;
  hint: string;
  warning: string;
}

export type AccountKind = 'personal' | 'team';
export type AccountStatus = 'active' | 'suspended' | 'closed';
export type Role = 'owner' | 'admin' | 'member' | 'readonly';

export interface Account {
  id: string;
  name: string;
  kind: AccountKind;
  status: AccountStatus;
  plan: string;
  role: Role;
}

export interface CurrentUser {
  user_id: string;
  /**
   * Display name. The only label available for a user who signed in with a
   * provider that has no email, such as WeChat or QQ.
   */
  name: string | null;
  /**
   * Contact address, not an identity. Null for providers that never supply
   * one -- render `name` in preference to this.
   */
  email: string | null;
  is_staff: boolean;
}

export interface Me {
  user: CurrentUser;
  accounts: Account[];
  /**
   * Which account the portal should show. Present so the SPA never has to
   * reason about *which* account it is in until there is more than one.
   */
  default_account_id: string | null;
}

/**
 * What a client needs to send its first request, resolved by the deployment.
 *
 * Both fields describe the api that answered, not the build. The inference
 * endpoint is a *different* public name from the portal — api and gate both
 * serve `/v1/`, so they cannot share an origin — and it is not derivable in
 * the browser. A client that guesses it documents a URL that 404s.
 */
export interface ServiceConfig {
  /** Base URL for OpenAI-compatible calls, with no trailing slash. */
  inference_base_url: string;
  /** Model ids the gate is actually serving. Empty if the gate was unreachable. */
  models: string[];
}

/**
 * Which sign-in methods a deployment actually offers.
 *
 * Both fields describe the api that answered, not the build: providers are
 * configured per deployment, and `dev_login` is routed only under
 * `--dev-login`. A client that assumes either one renders a button that 404s.
 */
export interface AuthMethods {
  /** OAuth providers, by name. Empty when none are configured. */
  providers: string[];
  /** Whether `POST /v1/auth/dev-login` exists. False in any production deploy. */
  dev_login: boolean;
}

/**
 * Usage, in the unit the service actually meters in.
 *
 * `peak_kv_tokens` is a high-water mark, not a sum: two concurrent 25k
 * sessions are not one 50k session.
 */
export interface UsageTotals {
  requests: number;
  /** Every prompt token, cached or not. `cached_input_tokens` is a subset of
   * this, so the two must never be added together. */
  input_tokens: number;
  /** Of `input_tokens`, how many were served from the prefix cache. Billed at
   * their own, lower rate: a cache hit skips prefill entirely. */
  cached_input_tokens: number;
  output_tokens: number;
  kv_token_seconds: number;
  peak_kv_tokens: number;
  /** What the rate card charges, in milli-credits (1 credit = 1000). Derived
   * from the token counts above at the current weights, never stored — see
   * `docs/pricing_design.md`. */
  credits_milli: number;
}

/** The three rolling windows the plan is sold in, narrowest first. */
export type CreditWindow = '5h' | '7d' | '30d';

/**
 * The token weights that define a credit, in micro-credits per token.
 *
 * Shipped rather than hard-coded in the UI so the portal can show the
 * arithmetic behind a number instead of asserting it, and so a re-weight does
 * not need a front-end deploy.
 */
export interface CreditWeights {
  micro_per_cached_input_token: number;
  micro_per_uncached_input_token: number;
  micro_per_output_token: number;
}

/**
 * One window's standing.
 *
 * `cap_milli` is null when the window is uncapped, which is what every plan
 * predating the token rate card is. Null is not zero: zero would mean nothing
 * is allowed.
 */
export interface CreditWindowUsage {
  window: CreditWindow;
  used_milli: number;
  cap_milli: number | null;
  /** When the window next frees credits, or null if nothing is in it. */
  frees_at: string | null;
}

/** What the account has spent against its plan, and what it has left. */
export interface CreditStanding {
  plan: string;
  windows: CreditWindowUsage[];
  weights: CreditWeights;
  /** Unexpired, undrawn credits from grants: plan allowance and top-ups. */
  grant_balance_milli: number;
  as_of: string;
}

export interface ApiErrorBody {
  error: { message: string; type: string; code?: string };
}

/** An error carrying the status, so callers can distinguish 401 from 500. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly type: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Not signed in — the caller should show the login page, not an error. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

const CSRF_COOKIE = 'xinfer-csrf';
const CSRF_HEADER = 'x-xinfer-csrf';

function csrfToken(): string {
  const match = document.cookie
    .split(';')
    .map((part) => part.trim().split('='))
    .find(([name]) => name === CSRF_COOKIE);
  return match?.[1] ?? '';
}

/**
 * The control-plane client.
 *
 * Same-origin by default, which is what lets the session cookie be
 * `SameSite=Strict` and removes CORS entirely. `credentials: 'same-origin'`
 * rather than `include`, so a misconfigured base URL fails loudly instead of
 * quietly sending cookies somewhere else.
 */
export class Client {
  constructor(private readonly baseUrl: string = '') {}

  protected get<T>(path: string): Promise<T> {
    return this.request('GET', path);
  }

  protected post<T>(path: string, body?: unknown): Promise<T> {
    return this.request('POST', path, body);
  }

  protected patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request('PATCH', path, body);
  }

  protected delete<T>(path: string): Promise<T> {
    return this.request('DELETE', path);
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {};
    if (body !== undefined) {
      headers['content-type'] = 'application/json';
    }
    // The double-submit half of the CSRF check. Reads do not need it, but
    // sending it always is simpler than deciding per call.
    const token = csrfToken();
    if (token) {
      headers[CSRF_HEADER] = token;
    }

    const response = await fetch(`${this.baseUrl}${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      body: body === undefined ? undefined : JSON.stringify(body),
    });

    if (!response.ok) {
      let type = 'unknown';
      let message = `${method} ${path} failed with ${response.status}`;
      try {
        const parsed = (await response.json()) as ApiErrorBody;
        type = parsed.error?.type ?? type;
        message = parsed.error?.message ?? message;
      } catch {
        // A non-JSON error body is still an error; the status is what matters.
      }
      throw new ApiError(response.status, type, message);
    }

    if (response.status === 204) {
      return undefined as T;
    }
    return (await response.json()) as T;
  }

  me(): Promise<Me> {
    return this.request('GET', '/v1/me');
  }

  /**
   * Which sign-in methods the api will actually accept.
   *
   * Render sign-in from this rather than from a build-time assumption: OAuth
   * providers are configured per deployment, and `dev-login` is not routed at
   * all unless the api was started with `--dev-login`.
   */
  authMethods(): Promise<AuthMethods> {
    return this.request('GET', '/v1/auth/providers');
  }

  /**
   * The inference base URL and the models behind it.
   *
   * Read this rather than deriving either from `window.location`: the gate is
   * a different public name from the portal, and the model list is whatever a
   * worker has registered.
   */
  serviceConfig(): Promise<ServiceConfig> {
    return this.request('GET', '/v1/config');
  }

  /**
   * A full-page navigation, not a fetch: the api answers with a 307 to the
   * provider, and the flow has to happen in the address bar so the provider
   * can show its own consent screen and return with a cookie on our origin.
   */
  loginUrl(provider: string): string {
    return `${this.baseUrl}/v1/auth/login/${encodeURIComponent(provider)}`;
  }

  devLogin(email: string): Promise<{ user_id: string; email: string }> {
    return this.request('POST', '/v1/auth/dev-login', { email });
  }

  logout(): Promise<{ signed_out: boolean }> {
    return this.request('POST', '/v1/auth/logout');
  }

  account(accountId: string): Promise<Account> {
    return this.request('GET', `/v1/accounts/${accountId}`);
  }

  listKeys(accountId: string): Promise<{ data: ApiKey[] }> {
    return this.request('GET', `/v1/accounts/${accountId}/keys`);
  }

  createKey(accountId: string): Promise<MintedKey> {
    return this.request('POST', `/v1/accounts/${accountId}/keys`);
  }

  revokeKey(accountId: string, keyId: string): Promise<{ revoked: boolean }> {
    return this.request('POST', `/v1/accounts/${accountId}/keys/${keyId}/revoke`);
  }

  usage(accountId: string): Promise<UsageTotals> {
    return this.request('GET', `/v1/accounts/${accountId}/usage`);
  }

  invoices(accountId: string): Promise<{ data: Invoice[] }> {
    return this.request('GET', `/v1/accounts/${accountId}/invoices`);
  }

  credits(accountId: string): Promise<CreditStanding> {
    return this.request('GET', `/v1/accounts/${accountId}/credits`);
  }
}

/** What the admin surface returns. Staff-only; see `AdminClient`. */
export interface AdminAccount extends Omit<Account, 'role'> {
  spend_cap_cents: number | null;
  created_at: string;
}

export interface InvoiceLine {
  metric: string;
  model_id: string;
  quantity: number;
  unit_micros: number;
  amount_micros: number;
}

export interface Invoice {
  id: number;
  account_id: string;
  period_start: string;
  period_end: string;
  price_version: number;
  status: string;
  total_micros: number;
  currency: string;
  lines: InvoiceLine[];
}

export interface AccountDetail {
  account: AdminAccount;
  keys: ApiKey[];
  usage: UsageTotals;
  invoices: Invoice[];
  /** Null when the standing could not be read. The page still loads without
   * it, because staff open it during incidents. */
  credits: CreditStanding | null;
}

export interface PolicyStatus {
  version: number;
  keys: number;
  revoked: number;
  issued_at: string;
}

/**
 * The fleet view is allowed to be unavailable — an ops view must never become
 * a dependency of the thing it watches — so `available` is part of the
 * successful response rather than signalled by an error status.
 */
export interface Fleet {
  available: boolean;
  reason?: string;
  metrics?: Record<string, number>;
}

export interface AuditEntry {
  actor: string;
  action: string;
  target: string | null;
  ip: string | null;
}

// ---------------------------------------------------------------------------
// The catalogue: products, models, rates, plans, promotions, subscriptions.
//
// A **product** is what is sold -- "DeepSeek-V4-Flash", "GLM-5.2". It owns the
// models it covers and the plans it is sold at, and quota is scoped to it: an
// account subscribed to two products holds two independent sets of credit
// windows. See `crates/xinfer-api/migrations/0008_products.sql`.
// ---------------------------------------------------------------------------

export type ProductStatus = 'draft' | 'active' | 'retired';
/** `draft` may be edited in place; from `published` on, every edit is a new version. */
export type PlanStatus = 'draft' | 'published' | 'retired';
export type SubscriptionStatus = 'active' | 'cancelled' | 'expired';
/** `price` discounts the subscription; `usage` discounts the credit burn rate. */
export type PromotionKind = 'price' | 'usage';

/** Credit weights for one model, at one version. Never edited in place: an
 *  invoice line pins the version it was computed at. */
export interface ModelRate {
  version: number;
  weights: {
    micro_per_cached_input_token: number;
    micro_per_uncached_input_token: number;
    micro_per_output_token: number;
  };
  /** What a credit costs beyond the allowance. Null falls back to the price
   *  version's global rate. */
  micros_per_credit: number | null;
}

export interface ProductModel {
  model_id: string;
  /** The one the portal offers first. At most one per product. */
  is_default: boolean;
  /** Null for a model nobody has priced, which bills at the global weights. */
  rate: ModelRate | null;
}

export interface Product {
  id: string;
  name: string;
  /** Stable and URL-safe. Renameable independently of `id`. */
  slug: string;
  status: ProductStatus;
  description: string;
  models: ProductModel[];
  created_at: string;
}

/** A plan as the catalogue sees it. `Plan` limits are flattened in, matching
 *  what the gate is sent. */
export interface PlanRecord {
  name: string;
  version: number;
  /** Null for the legacy product-less catalogue, which is what `default` is. */
  product_id: string | null;
  display_name: string | null;
  status: PlanStatus;
  limits: {
    max_concurrent_requests: number;
    max_context_tokens: number;
    max_concurrent_long_sessions: number;
    long_session_threshold_tokens: number;
    default_max_output_tokens: number;
    /** Milli-credits. Null means uncapped. */
    credits_5h_milli?: number | null;
    credits_7d_milli?: number | null;
    credits_30d_milli?: number | null;
  };
  price_cents_monthly: number | null;
  price_version: number;
  created_at: string;
}

export interface Promotion {
  id: number;
  name: string;
  kind: PromotionKind;
  /** Both null means service-wide. */
  scope_product_id: string | null;
  scope_plan_name: string | null;
  /** Whole percent, for a `price` promotion. */
  percent_off: number | null;
  /** Basis points of the normal burn rate, for a `usage` promotion. 10000 is
   *  no discount. */
  credit_multiplier_bp: number | null;
  starts_at: string;
  ends_at: string;
  /** `active` or `ended`. Whether it is *live* is this plus the window --
   *  a promotion that has not started yet is `active` but not live. */
  status: string;
  max_redemptions: number | null;
  note: string | null;
  created_at: string;
}

export interface Subscription {
  id: number;
  account_id: string;
  product_id: string | null;
  /** Which subscription pays when two of them cover the same model. Higher
   *  wins; ties break to the oldest subscription. */
  priority: number;
  plan_name: string;
  /** Pinned. Editing a plan writes a new version and leaves this alone. */
  plan_version: number;
  status: SubscriptionStatus;
  effective_from: string;
  effective_to: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancelled_at: string | null;
}

export interface CreditGrant {
  id: number;
  account_id: string;
  /** Null means spendable against any product. */
  product_id: string | null;
  /** `plan` | `topup` | `promo`. */
  kind: string;
  credits_milli: number;
  granted_at: string;
  expires_at: string | null;
  source: string;
  note: string | null;
}

/** One account's standing against one product's rate card. The per-product
 *  analogue of `CreditStanding`. */
export interface ProductStanding {
  product_id: string;
  product_name: string;
  plan: string;
  windows: CreditWindowUsage[];
  weights: CreditWeights;
  grant_balance_milli: number;
  /** Basis points. 10000 is no discount. */
  usage_multiplier_bp: number;
  as_of: string;
}

/** A separate client for a separate origin, so admin calls cannot be made by
 *  accident from the customer portal's code. */
export class AdminClient extends Client {
  accounts(query = ''): Promise<{ data: AdminAccount[] }> {
    return this.get(`/admin/v1/accounts?query=${encodeURIComponent(query)}`);
  }

  accountDetail(accountId: string): Promise<AccountDetail> {
    return this.get(`/admin/v1/accounts/${accountId}`);
  }

  suspend(accountId: string): Promise<{ status: string }> {
    return this.post(`/admin/v1/accounts/${accountId}/suspend`);
  }

  unsuspend(accountId: string): Promise<{ status: string }> {
    return this.post(`/admin/v1/accounts/${accountId}/unsuspend`);
  }

  lookupKey(keyId: string): Promise<ApiKey> {
    return this.get(`/admin/v1/keys/${keyId}`);
  }

  revokeKeyById(keyId: string): Promise<{ revoked: boolean }> {
    return this.post(`/admin/v1/keys/${keyId}/revoke`);
  }

  rateNow(accountId: string): Promise<Invoice> {
    return this.post(`/admin/v1/accounts/${accountId}/rate`);
  }

  policy(): Promise<PolicyStatus> {
    return this.get('/admin/v1/policy');
  }

  fleet(): Promise<Fleet> {
    return this.get('/admin/v1/fleet');
  }

  audit(): Promise<{ data: AuditEntry[] }> {
    return this.get('/admin/v1/audit');
  }
  // -------------------------------------------------------------------------
  // The catalogue.
  //
  // Note what is missing: there is no method that edits a published plan or a
  // model rate. Both are versioned because an invoice pins the version it was
  // computed at, so "save" is `createPlanVersion` / `createModelRate` and the
  // UI says so.
  // -------------------------------------------------------------------------

  products(): Promise<{ data: Product[] }> {
    return this.get('/admin/v1/products');
  }

  product(id: string): Promise<Product> {
    return this.get(`/admin/v1/products/${encodeURIComponent(id)}`);
  }

  createProduct(body: {
    name: string;
    slug: string;
    description?: string;
    status?: ProductStatus;
  }): Promise<Product> {
    return this.post('/admin/v1/products', body);
  }

  updateProduct(
    id: string,
    patch: { name?: string; description?: string; status?: ProductStatus },
  ): Promise<Product> {
    return this.patch(`/admin/v1/products/${encodeURIComponent(id)}`, patch);
  }

  attachModel(
    productId: string,
    body: { model_id: string; is_default?: boolean },
  ): Promise<ProductModel> {
    return this.post(`/admin/v1/products/${encodeURIComponent(productId)}/models`, body);
  }

  detachModel(productId: string, modelId: string): Promise<{ detached: boolean }> {
    return this.delete(
      `/admin/v1/products/${encodeURIComponent(productId)}/models/${encodeURIComponent(modelId)}`,
    );
  }

  modelRates(modelId: string): Promise<{ data: ModelRate[] }> {
    return this.get(`/admin/v1/models/${encodeURIComponent(modelId)}/rates`);
  }

  /** Writes the *next* rate version. Never an update -- an invoice line pins
   *  the version it was computed at. */
  createModelRate(
    modelId: string,
    body: {
      micro_per_cached_input_token: number;
      micro_per_uncached_input_token: number;
      micro_per_output_token: number;
      micros_per_credit?: number | null;
      note?: string;
    },
  ): Promise<ModelRate> {
    return this.post(`/admin/v1/models/${encodeURIComponent(modelId)}/rates`, body);
  }

  plans(productId?: string): Promise<{ data: PlanRecord[] }> {
    const query = productId ? `?product_id=${encodeURIComponent(productId)}` : '';
    return this.get(`/admin/v1/plans${query}`);
  }

  /** Creates a plan, or the next version of one that exists. There is no
   *  in-place edit: a subscription pins a plan version. */
  createPlanVersion(body: Record<string, unknown>): Promise<PlanRecord> {
    return this.post('/admin/v1/plans', body);
  }

  publishPlan(name: string, version: number): Promise<PlanRecord> {
    return this.post(`/admin/v1/plans/${encodeURIComponent(name)}/${version}/publish`);
  }

  retirePlan(name: string, version: number): Promise<PlanRecord> {
    return this.post(`/admin/v1/plans/${encodeURIComponent(name)}/${version}/retire`);
  }

  promotions(): Promise<{ data: Promotion[] }> {
    return this.get('/admin/v1/promotions');
  }

  createPromotion(body: {
    name: string;
    kind: PromotionKind;
    scope_product_id?: string | null;
    scope_plan_name?: string | null;
    percent_off?: number | null;
    credit_multiplier_bp?: number | null;
    starts_at: string;
    ends_at: string;
    max_redemptions?: number | null;
    note?: string | null;
  }): Promise<Promotion> {
    return this.post('/admin/v1/promotions', body);
  }

  /** The only edit a live promotion allows, and it only brings `ends_at`
   *  forward: rating re-reads the window when it re-rates a past period. */
  endPromotion(id: number): Promise<Promotion> {
    return this.post(`/admin/v1/promotions/${id}/end`);
  }

  subscriptions(
    accountId: string,
  ): Promise<{ data: Subscription[]; standings: ProductStanding[] }> {
    return this.get(`/admin/v1/accounts/${encodeURIComponent(accountId)}/subscriptions`);
  }

  /** Subscribe, or move to a different plan. One call for both: the store
   *  cancels any existing active subscription to the same product in the same
   *  transaction. */
  activateSubscription(
    accountId: string,
    body: { product_id: string; plan_name: string },
  ): Promise<Subscription> {
    return this.post(`/admin/v1/accounts/${encodeURIComponent(accountId)}/subscriptions`, body);
  }

  /** Set which subscription drains first when two of them cover one model. */
  setSubscriptionPriority(
    accountId: string,
    subscriptionId: number,
    priority: number,
  ): Promise<Subscription> {
    return this.post(
      `/admin/v1/accounts/${encodeURIComponent(accountId)}/subscriptions/${subscriptionId}/priority`,
      { priority },
    );
  }

  cancelSubscription(accountId: string, subscriptionId: number): Promise<Subscription> {
    return this.post(
      `/admin/v1/accounts/${encodeURIComponent(accountId)}/subscriptions/${subscriptionId}/cancel`,
    );
  }

  grants(accountId: string): Promise<{ data: CreditGrant[] }> {
    return this.get(`/admin/v1/accounts/${encodeURIComponent(accountId)}/grants`);
  }

  createGrant(
    accountId: string,
    body: {
      product_id?: string | null;
      kind: 'topup' | 'promo';
      credits_milli: number;
      expires_at?: string | null;
      note?: string | null;
    },
  ): Promise<CreditGrant> {
    return this.post(`/admin/v1/accounts/${encodeURIComponent(accountId)}/grants`, body);
  }
}


export const client = new Client();
export const adminClient = new AdminClient();
